/**
 * MoveStatusModal — super-admin dropdown to force-move a ride to any status,
 * OR jump straight into the full ride editor. Wired to
 * POST /api/nemt/admin/rides/{id}/set-status. Reason required for a status
 * move. Picking "Edit ride details" closes this modal and opens the Edit
 * modal instead (no status change, no reason required).
 */
import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, ArrowRightLeft, PencilLine } from "lucide-react";
import { adminPost } from "./utils";

// Grouped for readability in the dropdown. The `edit` sentinel value at the
// top opens the Edit modal instead of calling set-status.
const STATUS_OPTIONS = [
  { group: "Ride details", opts: [
    { v: "edit",             label: "Edit ride details (name, addresses, date/time, pricing…)" },
  ]},
  { group: "Booking states", opts: [
    { v: "requested",        label: "Requested" },
    { v: "pending_payment",  label: "Pending payment" },
    { v: "scheduled",        label: "Scheduled" },
    { v: "held",             label: "Held (action needed)" },
    { v: "insurance_pending",label: "Insurance pending" },
  ]},
  { group: "Trip states", opts: [
    { v: "assigned",         label: "Assigned" },
    { v: "en_route",         label: "En route" },
    { v: "arrived",          label: "Arrived at pickup" },
    { v: "in_progress",      label: "In progress" },
    { v: "completed",        label: "Completed" },
  ]},
  { group: "Closed states", opts: [
    { v: "cancelled",        label: "Cancelled" },
    { v: "no_show",          label: "No show" },
  ]},
];

export function MoveStatusModal({ auth, ride, onClose, onDone, onOpenEdit }) {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [movedBy, setMovedBy] = useState("");
  const [busy, setBusy] = useState(false);
  const isEdit = target === "edit";

  const submit = async (e) => {
    e.preventDefault();
    if (!target) { toast.error("Pick an action from the dropdown"); return; }
    // Edit path — hand off to the Edit modal, no status change.
    if (isEdit) {
      onClose && onClose();
      // Small tick so the modal state settles before opening the next one
      setTimeout(() => { onOpenEdit && onOpenEdit(ride); }, 0);
      return;
    }
    if (!reason.trim()) { toast.error("Reason is required for the audit log"); return; }
    setBusy(true);
    try {
      const r = await adminPost(auth, `rides/${ride.id}/set-status`, {
        status: target, reason: reason.trim(),
        moved_by: movedBy.trim() || "dispatcher",
      });
      toast.success(`Moved ${r.prev_status} → ${r.status}`);
      onDone && onDone();
    } catch (err) { toast.error(err.message || "Move failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="move-status-modal"
          onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-brand-deep">
              <ArrowRightLeft className="w-5 h-5 text-slate-700" />
              <h2 className="font-serif text-xl">Ride actions</h2>
            </div>
            <div className="text-xs text-brand-muted mt-1">
              {ride.member_name} · currently <strong>{ride.status}</strong>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-brand-muted hover:text-red-600" data-testid="move-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-[0.14em] text-brand-muted font-semibold block mb-1">
            Action
          </label>
          <select value={target} onChange={(e) => setTarget(e.target.value)}
            data-testid="move-target-select"
            className="w-full px-3 py-2.5 rounded-lg border border-brand-navy/15 bg-white">
            <option value="" disabled>— pick an action —</option>
            {STATUS_OPTIONS.map((g) => (
              <optgroup key={g.group} label={g.group}>
                {g.opts.filter((o) => o.v !== ride.status).map((o) => (
                  <option key={o.v} value={o.v}>{o.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {!isEdit && (
          <>
            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-brand-muted font-semibold block mb-1">
                Reason (audit log) *
              </label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                rows={3} required data-testid="move-reason"
                placeholder="e.g. 'Card hold declined — customer paying cash on pickup'"
                className="w-full px-3 py-2 rounded-lg border border-brand-navy/15 text-sm resize-none" />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-[0.14em] text-brand-muted font-semibold block mb-1">
                Moved by (your name, optional)
              </label>
              <input value={movedBy} onChange={(e) => setMovedBy(e.target.value)}
                placeholder="dispatcher" data-testid="move-by"
                className="w-full px-3 py-2 rounded-lg border border-brand-navy/15 text-sm" />
            </div>
          </>
        )}

        {isEdit && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900">
            Opens the full ride editor — customer info, pickup / dropoff (with autocomplete), date & time, trip class, mobility, notes, and pricing. Does <strong>not</strong> capture payment.
          </div>
        )}

        <button type="submit" disabled={busy || !target || (!isEdit && !reason.trim())}
          data-testid="move-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark disabled:opacity-50 text-white font-semibold py-2.5 rounded-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                : isEdit ? <PencilLine className="w-4 h-4" />
                : <ArrowRightLeft className="w-4 h-4" />}
          {isEdit ? "Open ride editor" : "Move ride"}
        </button>
      </form>
    </div>
  );
}
